import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { MdOutlineEmail, MdOutlineAccessTime, MdOutlineKeyboardArrowRight } from "react-icons/md";
import { LuPhone } from "react-icons/lu";
import Navbarsynergtech from "../Homepage/Navbarsynergtech/Navbarsynergtech";
import FooterAll from "../Homepage/FooterAll/FooterAll";
import Footer from "../Homepage/Footer/Footer";
import axiosInstance from "../api/axiosInstance";
import { trackEvent } from "../utils/trackEvent";
// Same page banner (background image, heading and breadcrumb styles) as About, Team, Portfolio, etc.
import "../AboutusPage/AboutusNavbar/AboutusNavbar.css";
import "./ContactPage.css";

const TITLE = "Contact Us | 360 SynergyTech";
const DESCRIPTION =
  "Contact 360 SynergyTech for custom software, AI, cloud and data engineering projects. Send us a message, email info@360synergytech.com or call us.";
const PAGE_URL = "https://360synergytech.com/contact";

const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

function ContactPage() {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", message: "" });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!validateEmail(formData.email)) newErrors.email = "Please enter a valid email";
    if (!formData.message.trim()) newErrors.message = "Message is required";

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setIsSubmitting(true);
    setStatusMessage(null);
    try {
      await axiosInstance.post("api/contact", formData);
      trackEvent("contact_form_submitted");
      setStatusMessage({ type: "success", text: "Your message has been sent successfully!" });
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      console.error("Error submitting contact form:", error);
      setStatusMessage({ type: "error", text: "Failed to send your message. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <Helmet>
        <title>{TITLE}</title>
        <meta name="description" content={DESCRIPTION} />
        <link rel="canonical" href={PAGE_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:image" content="https://360synergytech.com/synergy-logo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={TITLE} />
        <meta name="twitter:description" content={DESCRIPTION} />
        <meta name="twitter:image" content="https://360synergytech.com/synergy-logo.png" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://360synergytech.com/" },
              { "@type": "ListItem", position: 2, name: "Contact Us", item: PAGE_URL },
            ],
          })}
        </script>
      </Helmet>
      <Navbarsynergtech />

      <div className="main_div_aboutusnavbar">
        <h1 className="Heading_navbar" style={{ marginTop: 0, marginBottom: "1rem" }}>
          CONTACT US
        </h1>
        <div className="d-flex justify-content-center align-items-center gap-1">
          <Link to="/" className="para_aboutus_navbar_white text-decoration-none">
            Home
          </Link>
          <span className="para_aboutus_navbar_white">
            <MdOutlineKeyboardArrowRight />
          </span>
          <span className="para_aboutus_navbar_orange">Contact</span>
        </div>
      </div>

      <main className="main_div_contact_page">
        <div className="row m-0 p-0">
          <div className="col-lg-1 col-md-1 col-sm-12 col-12"></div>
          <div className="col-lg-10 col-md-10 col-sm-12 col-12">
            <p className="contact_page_tag">Get In Touch</p>
            <h2 className="contact_page_heading">Tell Us About Your Project</h2>
            <div className="contact_page_line" />
            <p className="contact_page_intro">
              Share a few details and our team will get back to you. You can also reach us directly by email or phone.
            </p>

            <div className="contact_page_grid">
              <aside className="contact_page_info_card">
                <h3 className="contact_page_info_heading">Contact Information</h3>
                <div className="contact_page_item">
                  <span className="contact_page_icon">
                    <MdOutlineEmail aria-hidden="true" />
                  </span>
                  <div>
                    <p className="contact_page_label">Email</p>
                    <a href="mailto:info@360synergytech.com">info@360synergytech.com</a>
                  </div>
                </div>
                <div className="contact_page_item">
                  <span className="contact_page_icon">
                    <LuPhone aria-hidden="true" />
                  </span>
                  <div>
                    <p className="contact_page_label">Phone</p>
                    <a href="tel:+923249925737">+92 324 9925737</a>
                  </div>
                </div>
                <div className="contact_page_item">
                  <span className="contact_page_icon">
                    <MdOutlineAccessTime aria-hidden="true" />
                  </span>
                  <div>
                    <p className="contact_page_label">Working Hours</p>
                    <p className="contact_page_value">Mon - Fri: 09.00am - 10.00 pm</p>
                  </div>
                </div>
                <Link to="/appointment" className="contact_page_appointment_link">
                  Prefer to book a call? Schedule an appointment
                </Link>
              </aside>

              <div className="contact_page_form_card">
                <h3 className="contact_page_form_heading">Send Us A Message</h3>
                <form onSubmit={handleSubmit} noValidate>
                  <div className="contact_form_group">
                    <label htmlFor="contact_name">Full Name</label>
                    <input
                      id="contact_name"
                      name="name"
                      className={errors.name ? "is-invalid" : ""}
                      placeholder="Enter your name"
                      value={formData.name}
                      onChange={handleInputChange}
                    />
                    {errors.name && <div className="contact_field_error">{errors.name}</div>}
                  </div>
                  <div className="contact_form_group">
                    <label htmlFor="contact_email">Email Address</label>
                    <input
                      id="contact_email"
                      name="email"
                      type="email"
                      className={errors.email ? "is-invalid" : ""}
                      placeholder="Enter your email"
                      value={formData.email}
                      onChange={handleInputChange}
                    />
                    {errors.email && <div className="contact_field_error">{errors.email}</div>}
                  </div>
                  <div className="contact_form_group">
                    <label htmlFor="contact_phone">Phone Number (Optional)</label>
                    <input
                      id="contact_phone"
                      name="phone"
                      placeholder="Enter your phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="contact_form_group">
                    <label htmlFor="contact_message">Your Message</label>
                    <textarea
                      id="contact_message"
                      name="message"
                      className={errors.message ? "is-invalid" : ""}
                      placeholder="How can we help?"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={5}
                    />
                    {errors.message && <div className="contact_field_error">{errors.message}</div>}
                  </div>

                  {statusMessage && (
                    <p className={statusMessage.type === "success" ? "contact_success_msg" : "contact_error_msg"}>
                      {statusMessage.text}
                    </p>
                  )}

                  <button type="submit" className="contact_submit_btn" disabled={isSubmitting}>
                    {isSubmitting ? "Sending..." : "Send Message"}
                  </button>
                </form>
              </div>
            </div>
          </div>
          <div className="col-lg-1 col-md-1 col-sm-12 col-12"></div>
        </div>
      </main>

      <FooterAll />
      <Footer />
    </div>
  );
}

export default ContactPage;
